import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import TextInput from "../ui/TextInput";
import StudioDateInput from "../ui/StudioDateInput";
import Dropdown from "../common/Dropdown";
import MultiSelectDropdown from "../common/MultiSelectDropdown";
import SimpleFormGroup from "../common/SimpleFormGroup";
import { getCompanyFileCategories } from "../../api/companyFileCategories";
import { CompanyFileCategory } from "../../types/requestsOutputs";
import {
    getCompanyFileTags,
    getCompanyUsageTypeMetadatas,
    getCompanyRightsTermMetadatas,
    getCompanyTerritoryMetadatas,
    getCompanyAllowedMediaMetadatas,
    CompanyFileTag,
    CompanyUsageTypeMetadata,
    CompanyRightsTermMetadata,
    CompanyTerritoryMetadata,
    CompanyAllowedMediaMetadata,
} from "../../api/companyMetadata";
import { LANGUAGES } from "../../utils/languages";
import { useLanguage } from "../../context/useLanguage";
import { useDatabase } from "../database/DatabaseContext";
import styles from "./FileMetadataModal.module.css";

interface FileMetadata {
    title?: string;
    description?: string;
    author?: string;
    date?: string;
    type?: string;
    language?: string;
    companyFileCategoryIds?: string[];
    companyRagFolderId?: string;
    companyFileTagIds?: string[];
    rightsOwner?: string;
    creditLine?: string;
    companyUsageTypeMetadataIds?: string[];
    companyRightsTermMetadataIds?: string[];
    companyTerritoryMetadataIds?: string[];
    companyAllowedMediaMetadataIds?: string[];
}

interface EditFileMetadataModalProps {
    isOpen: boolean;
    onClose: () => void;
    fileId: string;
    fileName: string;
    currentMetadata: FileMetadata;
    onUpdate: (fileId: string, metadata: FileMetadata) => void;
    onSave: (fileId: string, metadata: FileMetadata) => void;
    isLoading: boolean;
}

const EditFileMetadataModal: React.FC<EditFileMetadataModalProps> = ({
    isOpen,
    onClose,
    fileId,
    fileName,
    currentMetadata,
    onUpdate,
    onSave,
    isLoading,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { language } = useLanguage();
    const { folders } = useDatabase();
    const modalContentRef = useRef<HTMLDivElement>(null);

    const [categories, setCategories] = useState<CompanyFileCategory[]>([]);
    const [tags, setTags] = useState<CompanyFileTag[]>([]);
    const [usageTypes, setUsageTypes] = useState<CompanyUsageTypeMetadata[]>([]);
    const [rightsTerms, setRightsTerms] = useState<CompanyRightsTermMetadata[]>([]);
    const [territories, setTerritories] = useState<CompanyTerritoryMetadata[]>([]);
    const [allowedMedia, setAllowedMedia] = useState<CompanyAllowedMediaMetadata[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Local state for all fields to avoid auto-updating
    const [localMetadata, setLocalMetadata] = useState<FileMetadata>(currentMetadata);

    // Track if we've initialized for this modal session
    const initializedRef = useRef(false);
    // Track if metadata has been fetched to avoid re-fetching
    const metadataFetchedRef = useRef(false);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            // Portaled menus / date picker must not close the modal
            if (
                target.closest(".rw-select__menu") ||
                target.closest(".rw-select__menu-portal") ||
                target.closest(".react-select__menu") ||
                target.closest(".react-select__menu-portal") ||
                target.closest(".MuiPickersPopper-root") ||
                target.closest(".rw-studio-date-popper")
            ) {
                return;
            }

            if (modalContentRef.current && !modalContentRef.current.contains(target)) {
                onClose();
            }
        },
        [onClose],
    );

    // Initialize local state only when modal opens (not on every metadata change)
    useEffect(() => {
        if (isOpen && !initializedRef.current) {
            // Initialize local state with current metadata only once per modal session
            setLocalMetadata(currentMetadata);
            initializedRef.current = true;
            fetchAllMetadata();
        } else if (!isOpen) {
            // Reset the initialized flag when modal closes
            initializedRef.current = false;
            metadataFetchedRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, currentMetadata]);

    // Handle outside click listener separately
    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    const fetchAllMetadata = async () => {
        if (metadataFetchedRef.current) return; // Don't fetch if already fetched

        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No authentication token found");
            }
            const [categoriesData, tagsData, usageTypesData, rightsTermsData, territoriesData, allowedMediaData] =
                await Promise.all([
                    getCompanyFileCategories({ token }),
                    getCompanyFileTags({ token }),
                    getCompanyUsageTypeMetadatas({ token }),
                    getCompanyRightsTermMetadatas({ token }),
                    getCompanyTerritoryMetadatas({ token }),
                    getCompanyAllowedMediaMetadatas({ token }),
                ]);
            setCategories(categoriesData);
            setTags(tagsData);
            setUsageTypes(usageTypesData);
            setRightsTerms(rightsTermsData);
            setTerritories(territoriesData);
            setAllowedMedia(allowedMediaData);
            metadataFetchedRef.current = true; // Mark as fetched
        } catch (error) {
            console.error("Error fetching metadata:", error);
            setError(t("errorFetchingMetadata") || "Error fetching metadata");
        } finally {
            setLoading(false);
        }
    };

    const handleMetadataChange = (field: keyof FileMetadata, value: string | string[]) => {
        // Only update local state, don't call onUpdate until save
        setLocalMetadata((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No authentication token found");
            }

            // Prepare metadata for backend following the rules:
            // - undefined → don't touch
            // - null or "" → clear
            // - value → update
            // - [] for categories → clear categories
            const updateData: FileMetadata = {};

            // Use local metadata instead of current metadata
            Object.keys(localMetadata).forEach((key) => {
                const value = localMetadata[key as keyof FileMetadata];
                if (value !== undefined) {
                    if (
                        key === "companyFileCategoryIds" ||
                        key === "companyFileTagIds" ||
                        key === "companyUsageTypeMetadataIds" ||
                        key === "companyRightsTermMetadataIds" ||
                        key === "companyTerritoryMetadataIds" ||
                        key === "companyAllowedMediaMetadataIds"
                    ) {
                        // For array fields, pass as-is
                        updateData[key] = value as string[];
                    } else {
                        // For string fields, send null if empty, otherwise send the value
                        updateData[key as keyof FileMetadata] = (value === "" ? null : value) as any;
                    }
                }
            });

            // Call onUpdate with the final data first
            onUpdate(fileId, updateData);

            // Then call onSave
            onSave(fileId, updateData);
        } catch (error: any) {
            console.error("Error updating file metadata:", error);
            setError(error.message || t("errorUpdatingMetadata") || "Error updating metadata");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const modal = (
        <div className={styles.overlay}>
            <div className={styles.content} ref={modalContentRef}>
                <h1 className={styles.title}>
                    {t("editMetadata")}: {fileName}
                </h1>

                {error && <div className={styles.error}>{error}</div>}

                <div className={styles.body}>
                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>{t("generalMetadata")}</h2>

                        <div className={styles.grid}>
                            {/* Title, Author, Date in three columns */}
                            <SimpleFormGroup title={t("title") || "Title"} className={styles.formGroup}>
                                <TextInput
                                    placeholder={t("enterTitle") || "Enter title"}
                                    value={localMetadata.title || ""}
                                    onChange={(e) => handleMetadataChange("title", e.target.value)}
                                    size="small"
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup title={t("author") || "Author"} className={styles.formGroup}>
                                <TextInput
                                    placeholder={t("enterAuthor") || "Enter author"}
                                    value={localMetadata.author || ""}
                                    onChange={(e) => handleMetadataChange("author", e.target.value)}
                                    size="small"
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup title={t("date") || "Date"} className={styles.formGroup}>
                                <StudioDateInput
                                    value={localMetadata.date || ""}
                                    onChange={(isoDate) => handleMetadataChange("date", isoDate)}
                                    placeholder={t("selectDate") || "Select date"}
                                />
                            </SimpleFormGroup>

                            {/* Description spans 2 columns */}
                            <SimpleFormGroup
                                title={t("description") || "Description"}
                                className={`${styles.formGroup} ${styles.halfWidth}`}
                            >
                                <TextInput
                                    placeholder={t("enterDescription") || "Enter description"}
                                    value={localMetadata.description || ""}
                                    onChange={(e) => handleMetadataChange("description", e.target.value)}
                                    size="big-with-border"
                                    numberOfLines={3}
                                />
                            </SimpleFormGroup>

                            {/* Language */}
                            <SimpleFormGroup title={t("language") || "Language"} className={styles.formGroup}>
                                <Dropdown
                                    label=""
                                    value={localMetadata.language}
                                    options={LANGUAGES.map((lang) => ({
                                        value: lang.code,
                                        label:
                                            language === "FR"
                                                ? `${lang.labelFr} (${lang.nativeLabel})`
                                                : `${lang.label} (${lang.nativeLabel})`,
                                    }))}
                                    onChange={(value) => handleMetadataChange("language", value)}
                                    placeholder={t("selectLanguage") || "Select language"}
                                    loading={false}
                                    error={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            {/* Type, Folder in remaining columns */}
                            <SimpleFormGroup title={t("type") || "Type"} className={styles.formGroup}>
                                <Dropdown
                                    label=""
                                    value={localMetadata.type || ""}
                                    options={[
                                        { value: "text", label: t("text") },
                                        { value: "image", label: t("image") },
                                        { value: "audio", label: t("audio") },
                                        { value: "video", label: t("video") },
                                    ]}
                                    onChange={(value) => handleMetadataChange("type", value)}
                                    placeholder={t("selectType") || "Select content type"}
                                    loading={false}
                                    error={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup
                                title={t("folderDB") || "Folder"}
                                className={`${styles.formGroup} ${styles.halfWidth}`}
                            >
                                <Dropdown
                                    label=""
                                    value={localMetadata.companyRagFolderId || ""}
                                    options={folders.map((folder) => ({
                                        value: folder.id,
                                        label: folder.name,
                                    }))}
                                    onChange={(value) => handleMetadataChange("companyRagFolderId", value)}
                                    placeholder={t("selectFolder") || "Select folder (optional)"}
                                    loading={false}
                                    error={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            {/* Categories and Tags */}
                            <SimpleFormGroup title={`${t("tag")} 1`} className={styles.formGroup}>
                                <MultiSelectDropdown
                                    label=""
                                    value={localMetadata.companyFileCategoryIds || []}
                                    options={categories.map((category) => ({
                                        value: category.id,
                                        label: category.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyFileCategoryIds", values)}
                                    placeholder={`${t("select")} ${t("tag")} 1`}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup
                                title={`${t("tag")} 2`}
                                className={`${styles.formGroup} ${styles.halfWidth}`}
                            >
                                <MultiSelectDropdown
                                    label=""
                                    value={localMetadata.companyFileTagIds || []}
                                    options={tags.map((tag) => ({
                                        value: tag.id,
                                        label: tag.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyFileTagIds", values)}
                                    placeholder={`${t("select")} ${t("tag")} 2`}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                />
                            </SimpleFormGroup>
                        </div>
                    </div>

                    {/* LEGAL METADATA SECTION */}
                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>{t("legalMetadata")}</h2>

                        <div className={styles.grid}>
                            {/* Rights Owner, Credit Line, Usage Type in three columns */}
                            <SimpleFormGroup title={t("rightsOwner") || "Rights Owner"} className={styles.formGroup}>
                                <TextInput
                                    placeholder={t("enterRightsOwner") || "Enter rights owner"}
                                    value={localMetadata.rightsOwner || ""}
                                    onChange={(e) => handleMetadataChange("rightsOwner", e.target.value)}
                                    size="small"
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup title={t("creditLine") || "Credit Line"} className={styles.formGroup}>
                                <TextInput
                                    placeholder={t("enterCreditLine") || "Enter credit line"}
                                    value={localMetadata.creditLine || ""}
                                    onChange={(e) => handleMetadataChange("creditLine", e.target.value)}
                                    size="small"
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup
                                title={t("metadataUsageType") || "Usage Type"}
                                className={styles.formGroup}
                            >
                                <MultiSelectDropdown
                                    label=""
                                    value={localMetadata.companyUsageTypeMetadataIds || []}
                                    options={usageTypes.map((type) => ({
                                        value: type.id,
                                        label: type.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyUsageTypeMetadataIds", values)}
                                    placeholder={t("selectUsageTypes") || "Select usage types"}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            {/* Rights Term, Territory, Allowed Media in three columns */}
                            <SimpleFormGroup
                                title={t("metadataRightsTerm") || "Rights Term"}
                                className={styles.formGroup}
                            >
                                <MultiSelectDropdown
                                    label=""
                                    value={localMetadata.companyRightsTermMetadataIds || []}
                                    options={rightsTerms.map((term) => ({
                                        value: term.id,
                                        label: term.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyRightsTermMetadataIds", values)}
                                    placeholder={t("selectRightsTerms") || "Select rights terms"}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup title={t("metadataTerritory") || "Territory"} className={styles.formGroup}>
                                <MultiSelectDropdown
                                    label=""
                                    value={localMetadata.companyTerritoryMetadataIds || []}
                                    options={territories.map((territory) => ({
                                        value: territory.id,
                                        label: territory.name,
                                    }))}
                                    onChange={(values) => handleMetadataChange("companyTerritoryMetadataIds", values)}
                                    placeholder={t("selectTerritories") || "Select territories"}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>

                            <SimpleFormGroup
                                title={t("metadataAllowedMedia") || "Allowed Media"}
                                className={styles.formGroup}
                            >
                                <MultiSelectDropdown
                                    label=""
                                    value={localMetadata.companyAllowedMediaMetadataIds || []}
                                    options={allowedMedia.map((media) => ({
                                        value: media.id,
                                        label: media.name,
                                    }))}
                                    onChange={(values) =>
                                        handleMetadataChange("companyAllowedMediaMetadataIds", values)
                                    }
                                    placeholder={t("selectAllowedMedia") || "Select allowed media"}
                                    loading={loading}
                                    error={false}
                                    disabled={false}
                                    isWithoutMargin
                                />
                            </SimpleFormGroup>
                        </div>
                    </div>
                </div>

                <div className={styles.buttons}>
                    <Button
                        variant="primary"
                        label={loading || isLoading ? t("saving") || "Saving..." : t("save") || "Save"}
                        onClick={handleSave}
                        disabled={loading || isLoading}
                    />
                    <Button variant="cancel" label={t("cancel") || "Cancel"} onClick={onClose} />
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default EditFileMetadataModal;
